import { canResolveStaffing } from '@projektverwaltung/shared';
import type {
  KlStaffingBoardItem,
  StaffingFilterStatus,
  StaffingMaske as StaffingMaskeDaten,
} from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/**
 * Staffing neu (Issue #1) — kuratierte, KL-zentrierte Staffing-Ansicht.
 * Eine Karte je Projekt: Kopf (PXXXXXX – Name, Account, verlinkt) + „Wieder fällig am",
 * die Staffing-Maske (#158/#163: Positionen, Besetzungen, „Nicht zugeordnet"; den Baum
 * liefert /kl-board gesammelt mit), Staffing-Kommentare (letzte 2), Leerzustand
 * (Operating-/Intake-Notizen). Hinweis „Alles erledigt — Staffing abschließen?" im Kartenkopf.
 * Standard-Header + Filterleiste (Chips) wie Staffing/Phase Board.
 * Hinter Feature-Flag `staffing_neu` (Routing/Sidebar).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/toast.js';
import { AssignableRow } from '../../components/v2/assignable-row.js';
import { Badge } from '../../components/v2/badge.js';
import { Card } from '../../components/v2/card.js';
import { useConfirm } from '../../components/v2/confirm-dialog.js';
import { ProjectFilterBar } from '../../components/v2/filters/project-filter-bar.js';
import type { AssignableUser } from '../../components/v2/inline-assignee.js';
import { KlFollowupDateEditor } from '../../components/v2/kl-followup-date-editor.js';
import { LastCommentPreview } from '../../components/v2/last-comment-preview.js';
import { StaffingComments } from '../../components/v2/staffing-comments.js';
import { StaffingMaske } from '../../components/v2/staffing-maske/staffing-maske.js';
import { UebergabeNotiz } from '../../components/v2/uebergabe-notiz.js';
import { api } from '../../lib/api.js';
import { formatIsoDate } from '../../lib/format-date.js';
import {
  canManageStaffingFor,
  lebenszyklusOderNurLesbar,
  useCanAssignLeadManager,
} from '../../lib/permissions.js';
import { useReaderMode } from '../../lib/reader.js';
import { useSession } from '../../lib/session.js';
import { useProjectFilters } from '../../lib/use-project-filters.js';

// Modul-Ebene (stabile Referenz fuer useMemo im Hook, #114). #158: ein Projekt passt, wenn
// eine seiner Positionen den berechneten Status hat (#163: `zu_bestaetigen` = KI-Vorschlag);
// „offen" passt zusätzlich, wenn aktive Besetzungen nicht zugeordnet sind (Arbeit ohne Position).
const staffingStatusMatch = (i: KlStaffingBoardItem, s: StaffingFilterStatus) =>
  i.maske.positionen.some((p) => p.status_anzeige === s) ||
  (s === 'offen' && i.maske.nicht_zugeordnet.besetzungen.length > 0);

/**
 * Offene bestätigte Positionen (Kopf-Badge, Abschluss-Rückfrage). KI-Vorschläge haben
 * `zu_bestaetigen`, nie `offen` — sie zählen hier nicht.
 */
export function offenAnzahl(m: StaffingMaskeDaten): number {
  return m.positionen.filter((p) => p.status_anzeige === 'offen').length;
}

/** #163 (Spec 3.2): aktive Besetzungen ohne Position. */
export function nichtZugeordnetAnzahl(m: StaffingMaskeDaten): number {
  return m.nicht_zugeordnet.besetzungen.length;
}

/**
 * @param showHeader Default `true` (eigenständige Page). Im Tab der KFML-Worklist
 *   (PV-016) `false`: die Tab-Leiste trägt dort bereits die Überschrift, ein zweiter
 *   H1-Block auf derselben Seite wäre falsch.
 */
export function StaffingNeuPage({ showHeader = true }: { showHeader?: boolean } = {}) {
  const { t } = useTranslation();
  const { user } = useSession();

  const canResolve = user ? canResolveStaffing(user) : false;

  // Immer das volle Board laden (kein Segment-Param) → Segment-Filter clientseitig.
  // Sonst kollabieren die Segment-Optionen auf das gewählte Segment (sie werden aus
  // den geladenen Karten ermittelt). So bleiben die Optionen über alle Bedarfe stabil.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['v2-kl-staffing'],
    queryFn: () => api.getKlStaffingBoard(),
  });
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const users = (usersQuery.data?.items ?? []) as AssignableUser[];

  const allItems = data?.items ?? [];

  // Namen fuer die Personen-Filter (PL, Manager) — aus der VOLLEN Nutzerliste,
  // nicht aus den geladenen Karten (sonst fehlten Namen fuer Personen ohne Karte).
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.user_id, u.name);
    return map;
  }, [users]);

  // Filterzustand, Optionen, Filterung und Sortierung — gemeinsamer Hook mit dem
  // Worklist-Tab (#114). `staffingStatusMatch` ist eine Modul-Konstante, damit der
  // Hook seine `filtered`-Berechnung nicht bei jedem Render neu aufsetzt.
  // Abgeschlossene Projekte liefert das Backend gar nicht erst (staffing_completed_at
  // IS NULL) — ein Projekt verlässt die Liste also nur über den expliziten
  // „Staffing abschließen"-Button, nicht automatisch über einen Filter hier.
  const filters = useProjectFilters({
    view: 'staffing',
    items: allItems,
    nameById,
    statusMatch: staffingStatusMatch,
  });
  const items = filters.filtered;

  const segmentCount = useMemo(
    () => new Set(items.map((i) => i.project.segment).filter(Boolean)).size,
    [items],
  );

  return (
    <div className="max-w-[var(--canvas-max)] mx-auto px-2 pb-12 space-y-6">
      {showHeader && <Header projects={items.length} segments={segmentCount} />}

      <ProjectFilterBar filters={filters} showStatus />

      {isError && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {t(
              'v2.staffing_neu.load_failed',
              'Die Staffing-Liste konnte nicht geladen werden. Bitte Seite neu laden.',
            )}
          </p>
        </Card>
      )}

      {isLoading && (
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.detail.loading', 'Lädt…')}
        </p>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.staffing_neu.empty', 'Keine Projekte mit Positionen oder Wiedervorlage.')}
          </p>
        </Card>
      )}

      {items.map((item) => (
        <ProjectCard
          key={item.project.project_id}
          item={item}
          users={users}
          canResolve={canResolve}
          canManage={user ? canManageStaffingFor(user, scopeOf(item)) : false}
        />
      ))}
    </div>
  );
}

// ===========================================================================
// Header — Standard-Eyebrow/Headline/Subtitle. Die frühere „Neu"-Aktion (Bedarf per Dialog
// für ein beliebiges Projekt) entfällt mit #158: Bedarfe entstehen an einer Position der Maske.
// ===========================================================================

function Header({ projects, segments }: { projects: number; segments: number }) {
  const { t } = useTranslation();
  return (
    <header className="pt-6 animate-fade-up flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div
          className="text-[10.5px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.staffing_neu.eyebrow', 'Staffing · Kaufm. Leitung')}
        </div>
        <h1
          className="font-medium leading-[1.05] tracking-tight"
          style={{
            fontFamily: 'var(--f-display)',
            color: 'var(--ink)',
            fontSize: 'clamp(22px, 2.4vw, 36px)',
          }}
        >
          {projects}{' '}
          <em
            className="not-italic"
            style={{ color: 'var(--primary)', fontStyle: 'italic', fontWeight: 400 }}
          >
            {t('v2.staffing_neu.headline_emphasis', 'Projekte im Blick')}
          </em>{' '}
          · {t('v2.atlas.filter_segment', 'Segment')}: {segments}
        </h1>
        <p className="text-sm mt-2 max-w-[640px]" style={{ color: 'var(--ink-3)' }}>
          {t(
            'v2.staffing_neu.subtitle',
            'Kuratierte KL-Sicht: Projekte mit offenen Positionen, KI-Vorschlägen zum Bestätigen oder Wiedervorlage.',
          )}
        </p>
      </div>
    </header>
  );
}

// Projekt → ProjectLike-Scope für die Rechte-Helper (Board liefert die Felder mit).
function scopeOf(item: KlStaffingBoardItem) {
  const p = item.project;
  return {
    project_id: p.project_id,
    segment: p.segment ?? '',
    project_lead_user_id: p.project_lead_user_id ?? null,
    manager_user_id: p.manager_user_id ?? null,
    mtb_user_id: p.mtb_user_id ?? null,
    responsibility: p.responsibility ?? null,
    lifecycle_status: lebenszyklusOderNurLesbar(p.lifecycle_status),
  };
}

function ProjectCard({
  item,
  users,
  canResolve,
  canManage,
}: {
  item: KlStaffingBoardItem;
  users: Array<AssignableUser>;
  canResolve: boolean;
  canManage: boolean;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const p = item.project;
  // Klapp-Zustand flüchtig, Default eingeklappt — wie die Übergabe-Ansicht (übersichtlich).
  const [expanded, setExpanded] = useState(false);

  const maske = item.maske;
  const openCount = offenAnzahl(maske);
  const nzCount = nichtZugeordnetAnzahl(maske);
  // #163: KI-Vorschläge — „zu bestätigen" muss schon eingeklappt auffallen (Spec 4.2).
  const zuBestaetigen = maske.zu_bestaetigen;
  const leer =
    maske.positionen.length === 0 &&
    nzCount === 0 &&
    maske.nicht_zugeordnet.sf_projektbesetzungen.length === 0;
  const followup = p.kl_followup_date ? formatIsoDate(p.kl_followup_date, i18n.language) : null;
  // #54: Name des zugewiesenen Projektleiters aus der bereits geladenen
  // users-Liste auflösen (kein zusätzlicher Query je Karte).
  const leadName = p.project_lead_user_id
    ? (users.find((u) => u.user_id === p.project_lead_user_id)?.name ?? null)
    : null;

  const isReader = useReaderMode();
  const canEdit = !isReader && p.lifecycle_status === 'active';
  const canAssign = useCanAssignLeadManager({
    ...scopeOf(item),
    phase_is_intake: p.phase_is_intake ?? false,
  });
  const assignLead = useMutation({
    mutationFn: (uid: string | null) =>
      api.patchProject(p.project_id, { project_lead_user_id: uid }),
    onSuccess: () => {
      toast.success(t('v2.detail.assign_saved', 'Zuweisung gespeichert'));
      void qc.invalidateQueries({ queryKey: ['v2-kl-staffing'] });
    },
    onError: (e) =>
      toast.error(
        t('v2.detail.assign_failed', 'Zuweisung fehlgeschlagen'),
        e instanceof Error ? e.message : 'Fehler',
      ),
  });

  const complete = useMutation({
    mutationFn: () => api.completeStaffing(p.project_id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['v2-kl-staffing'] });
      toast.success(t('v2.staffing.completed_ok', 'Staffing abgeschlossen'));
    },
    onError: (err) =>
      toast.error(
        t('v2.staffing.complete_failed', 'Abschluss fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const onComplete = () => {
    // Bestätigung nur, wenn noch etwas offen, nicht zugeordnet oder (#163) zu bestätigen ist
    // (Punkt 2, #158).
    const nochOffen = openCount + nzCount + zuBestaetigen;
    if (nochOffen === 0) {
      complete.mutate();
      return;
    }
    void confirmAction({
      title: t('v2.staffing.complete_title', 'Staffing abschließen?'),
      body: t(
        'v2.staffing_maske.abschliessen_offen',
        'Es ist noch etwas offen, nicht zugeordnet oder zu bestätigen ({{n}}). Staffing trotzdem abschließen? Das Projekt verschwindet aus der Liste.',
        { n: nochOffen },
      ),
      confirmLabel: t('v2.staffing.complete', 'Staffing abschließen'),
    }).then((ok) => {
      if (ok) complete.mutate();
    });
  };

  return (
    <Card padding={false}>
      {/* Dichte Kopfzeile — gesamte Zeile klickt auf/zu. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        className="w-full p-4 cursor-pointer select-none"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-xs shrink-0"
            style={{
              color: 'var(--ink-3)',
              transform: expanded ? 'none' : 'rotate(-90deg)',
              transition: 'transform 120ms',
            }}
          >
            ▾
          </span>
          <Link
            to={`/projects/${p.project_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm font-medium hover:underline shrink-0"
            style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)', fontWeight: 600 }}
          >
            {p.project_key}
          </Link>
          <span className="text-sm font-medium truncate" style={{ color: 'var(--ink)' }}>
            {p.customer_name}
          </span>
          <span className="text-[11px] truncate hidden md:inline" style={{ color: 'var(--ink-3)' }}>
            {p.name}
          </span>
          {/* #54: Projektleiter schon eingeklappt sichtbar. Ohne zugewiesenen PL
              wird nichts gerendert — ein Platzhalter würde einen Namen vortäuschen. */}
          {leadName && (
            <span
              className="text-[11px] truncate hidden lg:inline"
              style={{ color: 'var(--ink-2)' }}
              title={t('v2.staffing.project_lead', 'Projektleiter')}
            >
              👤 {leadName}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {/* #58: Direktabsprung nach Salesforce, wie im Worklist-Tab. */}
            {p.salesforce_url && (
              <a
                href={p.salesforce_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[11px] hover:underline"
                style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)' }}
                title={t('v2.staffing.salesforce_link', 'In Salesforce öffnen')}
              >
                SF ↗
              </a>
            )}
            {/* #158: Hinweis, sobald jede Position erledigt/ausgeplant und nichts offen
                nicht zugeordnet ist — der Abschluss bleibt eine Handlung (Knopf). */}
            {maske.alles_erledigt && canResolve && (
              <span className="flex items-center gap-2 text-[11px]">
                <span style={{ color: 'var(--success)' }}>
                  {t('v2.staffing_maske.alles_erledigt', 'Alles erledigt — Staffing abschließen?')}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    complete.mutate();
                  }}
                  // Enter/Leertaste nicht an die Kopfzeile weiterreichen (die klappt sonst
                  // auf/zu und verhindert per preventDefault den Klick).
                  onKeyDown={(e) => e.stopPropagation()}
                  disabled={complete.isPending}
                  className="text-xs px-2 py-0.5 rounded"
                  style={{
                    background: 'var(--success-soft)',
                    color: 'var(--success)',
                    border: '1px solid var(--success)',
                    cursor: complete.isPending ? 'not-allowed' : 'pointer',
                  }}
                >
                  ✓ {t('v2.staffing.complete', 'Staffing abschließen')}
                </button>
              </span>
            )}
            {zuBestaetigen > 0 && (
              <Badge tone="warn">
                ⚠ {t('v2.staffing_maske.zu_bestaetigen_anzahl', { n: zuBestaetigen })}
              </Badge>
            )}
            {openCount > 0 && (
              <Badge tone="warn">
                {t('v2.staffing_maske.offen_anzahl', '{{n}} offen', { n: openCount })}
              </Badge>
            )}
            {nzCount > 0 && (
              <Badge tone="warn">
                {t('v2.staffing_maske.nz_anzahl', '{{n}} nicht zugeordnet', { n: nzCount })}
              </Badge>
            )}
            <span
              className="text-[11px]"
              style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
            >
              {t('v2.staffing_maske.positionen_anzahl', '{{n}} Positionen', {
                // KI-Vorschläge zählen erst nach dem Bestätigen als Position (#163).
                n: maske.positionen.length - zuBestaetigen,
              })}
            </span>
            {followup && (
              <span
                className="text-[11px]"
                style={{ color: 'var(--ink-2)', fontFamily: 'var(--f-mono)' }}
                title={t('v2.staffing.followup', 'Wieder fällig am')}
              >
                ⏰ {followup}
              </span>
            )}
          </div>
        </div>

        {/* Letzter Staffing-Kommentar schon in der EINGEKLAPPTEN Zeile — beim Überfliegen
            sieht man, wo ein Projekt im Staffing steht, ohne jede Karte aufzuklappen.
            `last_comments` liefert die Sammel-Query /kl-board bereits mit (die zwei neuesten,
            [0] ist der aktuellste) → kein Nachladen je Karte. Aufgeklappt zeigt
            StaffingComments den Kommentar ohnehin → nur bei `!expanded`. Ohne Kommentar:
            nichts (kein Platzhalter, ruhigere Liste). */}
        {!expanded && item.last_comments[0] && (
          <LastCommentPreview comment={item.last_comments[0]} />
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--line-soft)' }}>
          {/* #158: Übergabe-Notiz (= Intake „Sonstiges") oben in der Karte; Daten kommen mit
              dem Board (`project.intake_handover_fields`), keine eigene Abfrage. */}
          {/* Projektleiter änderbar (KFML-Wunsch 03.11.) — dieselbe Rechtefrage und derselbe
              Sperrhinweis wie im Projektdetail (#78, `assign_lead_manager`). */}
          <div className="pt-3 max-w-sm">
            <AssignableRow
              label={t('v2.staffing.project_lead', 'Projektleiter')}
              userId={p.project_lead_user_id}
              users={users}
              canEdit={canEdit && canAssign}
              disabledReason={
                canEdit && !canAssign
                  ? t(
                      'v2.detail.rail_assign_locked',
                      'In Phase „Neu" nur durch die kaufmännische Leitung',
                    )
                  : undefined
              }
              required
              onAssign={(uid) => assignLead.mutate(uid)}
            />
          </div>
          <div className="pt-3">
            <UebergabeNotiz
              project={scopeOf(item)}
              sonstiges={p.intake_handover_fields?.uebergabe_intake?.sonstiges}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-3">
            <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
              {t('v2.staffing.followup', 'Wieder fällig am')}
            </span>
            <KlFollowupDateEditor
              projectId={p.project_id}
              value={p.kl_followup_date}
              canEdit={canResolve || canManage}
            />
            {canResolve && (
              <button
                type="button"
                onClick={onComplete}
                disabled={complete.isPending}
                title={t(
                  'v2.staffing.complete_hint',
                  'Staffing für dieses Projekt abschließen — verschwindet aus der Liste',
                )}
                className="ml-auto text-xs px-2.5 py-1 rounded"
                style={{
                  background: 'var(--success-soft)',
                  color: 'var(--success)',
                  border: '1px solid var(--success)',
                  cursor: complete.isPending ? 'not-allowed' : 'pointer',
                  opacity: complete.isPending ? 0.6 : 1,
                }}
              >
                ✓ {t('v2.staffing.complete', 'Staffing abschließen')}
              </button>
            )}
          </div>

          {leer && (
            <div className="mt-2">
              <EmptyState project={p} />
            </div>
          )}

          {/* #158: Positionen, Besetzungen und „Nicht zugeordnet" — der Baum kommt
              gesammelt aus /kl-board, die Maske lädt deshalb nicht selbst. */}
          <div className="mt-3">
            <StaffingMaske projectId={p.project_id} project={scopeOf(item)} maske={maske} />
          </div>

          <div className="mt-3 pt-2 border-t" style={{ borderColor: 'var(--line-soft)' }}>
            <StaffingComments
              projectId={p.project_id}
              limit={2}
              canComment={canManage || canResolve}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

function EmptyState({ project }: { project: KlStaffingBoardItem['project'] }) {
  const { t } = useTranslation();
  const operating = project.salesforce_data?.projektnotizen;
  const intake = project.intake_handover_fields?.intake_meeting_notes;
  if (!operating && !intake) return null;
  return (
    <div className="text-xs flex flex-col gap-1.5" style={{ color: 'var(--ink-3)' }}>
      {operating && (
        <div style={{ color: 'var(--ink-2)' }}>
          <b>{t('v2.staffing.operating_notes', 'Projektnotizen Operating')}:</b> {operating}
        </div>
      )}
      {intake && (
        <div style={{ color: 'var(--ink-2)' }}>
          <b>{t('v2.staffing.intake_notes', 'Intake-Notizen')}:</b> {intake}
        </div>
      )}
    </div>
  );
}
